import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { BodyText, Div, GrowlNotification, GrowlNotificationSupportedVariant, Gutter, Loader, Tag } from '@gaiads/telia-react-component-library';
import {
  AssetAssignToOrganization,
  AssetAssignToOrganizationTranslations,
  AssetAssignToUser,
  AssetAssignToUserTranslations,
  createTicket,
  COUNTRIES,
  EolFlowFinishFromChoice,
  OrganizationAssetCard,
  OrganizationAssetCardTranslations,
  PageHeader,
  SelectedAssetAction,
  UserDetails,
} from '@cdaas/reusable-components-daas';
import {
  AdminAssetActionsResponse,
  AdminAssetManuallyReportedEndOfLeaseRequest,
  AdminOrganizationAssetResponse,
  AdminUserAssetResponse,
  AssetEndOfLeaseRequest,
  AssetMidLeaseRequest,
  AssetMidLeaseTerminationRequest,
  FinanceSyncStatus,
  InvoicingAccountOptionResponseContents,
  InvoicingAccountResponse,
  OrganizationWithUserGroupsResponse,
  SerialNumberRequestTypeEnum,
  UserWithUserGroupResponse,
} from '@cdaas/api-clients/dist/asset-admin-openapi';
import { User } from '@cdaas/reusable-components-daas/dist/src/asset-api-types/api-types';
import {
  assetAdminClient,
  assetAdminInvoicingAccountClient,
  assetAdminOrganizationClient,
  assetAdminUserClient,
  assetAdminUserGroupClient,
  assetEndOfLeaseClient,
  assetMidLeaseClient,
} from '../../api-client/asset-admin-client';
import { selectOrganization, setOrderOrganization } from '../../store/order';
import { selectFeatureFlags } from '../../store/featureFlags';
import { AppDispatch } from '../../store';
import { routes, withCustomerOrgFormPath } from '../App/routes';
import Message from '../App/Message';
import { ASSET_MAX_LIMIT, flattenOrganizationUsers } from '../../utils';
import { isRecurringMonthlyExtensionEnabled, useRecurringMonthlyExtensionsEnabled } from '../../utils/recurringMonthlyExtension';
import { MidLeaseChoiceTranslations, MidLeaseFlowFinishFromChoice } from '@cdaas/reusable-components-daas/dist/src/components/MidLeaseChoice/types';
import styles from './AssetDetails.module.scss';

export const AssetDetails = () => {
  const { t } = useTranslation('customerAssets');
  const { t: tGlobal } = useTranslation('global');
  const { t: tError } = useTranslation('errorCodes');
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();

  const useBuyoutWithPriceFlow = useSelector(selectFeatureFlags).FEATURE_FLAG_FRONTEND_FLOW_BUYOUT_WITH_PRICE_ENABLED;
  const recurringMonthlyExtensionsEnabled = useRecurringMonthlyExtensionsEnabled();

  const organizationId = useParams<{ id: string }>().id;
  const assetId = useParams<{ assetid: string }>().assetid;
  const organization: OrganizationWithUserGroupsResponse | null = useSelector(selectOrganization);

  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);
  const [asset, setAsset] = useState<AdminOrganizationAssetResponse>();
  const [userAssets, setUserAssets] = useState<AdminUserAssetResponse[]>([]);
  const [user, setUser] = useState<UserWithUserGroupResponse>();
  const [invoicingAccounts, setInvoicingAccounts] = useState<InvoicingAccountOptionResponseContents[]>([]);

  const [assignToDifferentUserModalOpen, setAssignToDifferentUserModalOpen] = useState(false);
  const [assignToOrganizationModalOpen, setAssignToOrganizationModalOpen] = useState(false);
  const [organizationUsers, setOrganizationUsers] = useState<User[]>([]);
  const [organizationUsersFetchError, setOrganizationUsersFetchError] = useState(false);
  const [organizationUsersLoading, setOrganizationUsersLoading] = useState(false);
  const [assetAssignLoading, setAssetAssignLoading] = useState(false);
  const [assetAssignSuccess, setAssetAssignSuccess] = useState(false);

  const [assetActions, setAssetActions] = useState<AdminAssetActionsResponse>();
  const [assetActionsLoading, setAssetActionsLoading] = useState(false);
  const [assetActionsPostError, setAssetActionsPostError] = useState(false);

  const [assetUpdatePutError, setAssetUpdatePutError] = useState(false);

  const [loadingInvoicingAccounts, setLoadingInvoicingAccounts] = useState(true);
  const [invoicingAccountsError, setInvoicingAccountsError] = useState(false);

  const [notification, setNotification] = useState<{ show: boolean; variant: GrowlNotificationSupportedVariant; text: string }>();

  const [assetSyncError, setAssetSyncError] = useState(false);

  const automaticExtension: boolean = asset?.prices?.lease?.can_lease_be_auto_extended ?? false;

  const contractEndsTitleMessage = t(
    isRecurringMonthlyExtensionEnabled(organization?.locale.country, recurringMonthlyExtensionsEnabled) && automaticExtension
      ? 'details.endOfLeaseNotifications.contractEndsTitleUpgrade'
      : 'details.endOfLeaseNotifications.contractEndsTitle',
  );

  const decisionNotYetDoneMessage = t(
    automaticExtension
      ? isRecurringMonthlyExtensionEnabled(organization?.locale.country, recurringMonthlyExtensionsEnabled)
        ? 'details.endOfLeaseNotifications.decisionNotYetDoneMessageExtensionRecurring'
        : 'details.endOfLeaseNotifications.decisionNotYetDoneMessageExtension'
      : 'details.endOfLeaseNotifications.decisionNotYetDoneMessageLeaseEnd',
  );

  const daysLeftForDecisionMessage = t(
    isRecurringMonthlyExtensionEnabled(organization?.locale.country, recurringMonthlyExtensionsEnabled)
      ? automaticExtension
        ? 'details.endOfLeaseNotifications.daysLeftForDecisionRecurring'
        : 'details.endOfLeaseNotifications.daysLeftForDecisionLast'
      : 'details.endOfLeaseNotifications.daysLeftForDecision',
  );

  const translations: OrganizationAssetCardTranslations = {
    validUntil: t('details.validUntil'),
    monthShort: t('details.monthShort'),
    monthsLeft: t('details.monthsLeft'),
    monthLeft: t('details.monthLeft'),
    vatInfo: t('details.vatIncluded'),
    replacedDeviceTag: t('details.replacedDeviceTag'),
    contractEndedDate: t('details.contractEndedDate'),
    deviceDetails: t('details.deviceDetails'),
    deviceBought: t('details.deviceBought'),
    deviceAwaitingReturn: t('details.deviceAwaitingReturn'),
    deviceReturnInTransit: t('details.deviceReturnInTransit'),
    deviceReturnReceived: t('details.deviceReturnReceived'),
    deviceReturnedApproved: t('details.deviceReturnedApproved'),
    deviceReturnedWithCloudLock: t('details.deviceReturnedWithCloudLock'),
    deviceReturnedWithConditions: t('details.deviceReturnedWithConditions'),
    deviceNotReturnedInvoiced: t('details.deviceNotReturnedInvoiced'),
    incorrectDeviceReturned: t('details.incorrectDeviceReturned'),
    otherReturnStatus: t('details.otherReturnStatus'),
    deviceReturned: t('details.deviceReturned'),
    deviceNotReturned: t('details.deviceNotReturned'),
    location: t('details.location'),
    locationReference: t('details.locationReference'),
    invoicingAccount: t('details.invoicingAccount'),
    activeInvoicingAccount: t('details.activeInvoicingAccount'),
    user: t('details.user'),
    userName: t('details.userName'),
    phoneNumber: t('details.phoneNumber'),
    otherDevices: t('details.otherDevices'),
    events: t('details.events'),
    tickets: t('details.tickets'),
    createTicketButton: t('details.createTicketButton'),
    createSecondTicketButton: t('details.createSecondTicketButton'),
    secondTicketModal: {
      title: t('details.secondTicketModal.title'),
      button: t('details.secondTicketModal.button'),
    },
    imeiNumber: t('details.imeiNumber'),
    previousImeis: t('details.previousImeis'),
    sku: t('details.sku'),
    bundleAccessories: t('details.bundleAccessories'),
    bundleOrderId: t('details.bundleOrderId'),
    bundleServices: t('details.bundleServices'),
    insurance: t('details.insurance'),
    ticketStatus: t('details.ticketStatus'),
    ticketOpen: t('details.ticketOpen'),
    ticketClosed: t('details.ticketClosed'),
    link: t('details.link'),
    contractEnded: t('details.contractEnded'),
    contractExtended: t('details.contractExtended'),
    endOfLeaseSelected: t('details.endOfLeaseSelected'),
    returnEnvelopeSent: t('details.returnEnvelopeSent'),
    endOfLeaseReturnColtHardwareDeliveryId: t('details.endOfLeaseReturnColtHardwareDeliveryId'),
    months: t('details.months'),
    delivered: t('details.delivered'),
    shipped: t('details.shipped'),
    companyDevice: tGlobal('companyDevice'),
    inactiveUser: tGlobal('inactiveUser'),
    endOfLeaseNotificationTranslations: {
      contractEndsTitle: contractEndsTitleMessage,
      decisionNotYetDoneMessage: decisionNotYetDoneMessage,
      daysLeftForDecision: daysLeftForDecisionMessage,
      buyoutTitle: t('details.endOfLeaseNotifications.buyoutTitle'),
      buyoutMessage: t('details.endOfLeaseNotifications.buyoutMessage'),
      returnTitle: t('details.endOfLeaseNotifications.returnTitle'),
      returnMessage: t('details.endOfLeaseNotifications.returnMessage'),
    },
    assignToDifferentUser: t('details.assignToDifferentUser'),
    assignToOrganization: t('details.assignToOrganization'),
    inactiveAssetInvoicingAccount: t('details.inactiveAssetInvoicingAccount'),
    eolSelectedBuyout: t('details.eolActionEventMessage.buyoutSelectedEvent'),
    eolSelectedReturn: t('details.eolActionEventMessage.returnDeviceEvent'),
    eolSelectedNotReturned: t('details.eolActionEventMessage.notReturnedEvent'),
    invoicingAccountSelection: {
      dropdownLabel: t('details.invoicingAccountSelection.dropdownLabel'),
      clearSelection: t('details.invoicingAccountSelection.clearSelection'),
      noOptionsFoundInfo: t('details.invoicingAccountSelection.noOptionsFoundInfo'),
      helperText: t('details.invoicingAccountSelection.helperText'),
      errorMessage: t('details.invoicingAccountSelection.errorMessage'),
      accountNotValidTitle: t('details.invoicingAccountSelection.accountNotValidTitle'),
      accountNotValidMessage: t('details.invoicingAccountSelection.accountNotValidMessage'),
    },
  };

  const midLeaseChoiceTranslations: MidLeaseChoiceTranslations = {
    buyoutWithoutPrice: {
      optionTitle: t('details.midLeaseBuyout.withoutPrice.optionTitle'),
      optionText: t('details.midLeaseBuyout.withoutPrice.optionText'),
      optionButton: t('details.midLeaseBuyout.withoutPrice.optionButton'),
      modalTitle: t('details.midLeaseBuyout.withoutPrice.modalTitle'),
      modalText1: t('details.midLeaseBuyout.withoutPrice.modalText1'),
      purchaseConfirmationCheckboxLabel: t('details.midLeaseBuyout.purchaseConfirmationCheckboxLabel'),
      confirmButton: t('details.midLeaseBuyout.confirmButton'),
      confirmedButton: t('details.midLeaseBuyout.withoutPrice.confirmedButton'),
      confirmedTitle: t('details.midLeaseBuyout.withoutPrice.confirmedTitle'),
      confirmedText: t('details.midLeaseBuyout.withoutPrice.confirmedText'),
      cancelButton: t('details.midLeaseBuyout.cancelButton'),
    },
    buyoutWithPrice: {
      buyoutDeviceOptionTitle: t('details.midLeaseBuyout.withPrice.purchaseDeviceOption'),
      buyoutDeviceOptionText: t('details.midLeaseBuyout.withPrice.purchaseDeviceText'),
      buyoutDeviceOptionWithPricePurchaseButton: t('details.midLeaseBuyout.withPrice.purchaseButton'),
      buyoutDeviceOptionWithPriceAskForPriceButton: t('details.midLeaseBuyout.withPrice.askForPriceButton'),
      buyoutDeviceOptionWithPricePurchaseAnywayButton: t('details.midLeaseBuyout.withPrice.purchaseAnywayButton'),
      buyoutDeviceOptionWithPriceName: t('details.midLeaseBuyout.withPrice.purchaseDevicePriceName'),
      buyoutDeviceOptionWithPriceIncludingVat: t('details.midLeaseBuyout.withPrice.purchaseDevicePriceIncludingVat'),
      buyoutDeviceOptionWithPriceExcludingVat: t('details.midLeaseBuyout.withPrice.purchaseDevicePriceExcludingVat'),
      buyoutDeviceOptionWithPriceWaitingForPrice: t('details.midLeaseBuyout.withPrice.waitingForPrice'),
      buyoutDeviceWithPriceWaitingPriceNotificationTitle: t('details.endOfLeaseNotifications.waitingForBuyoutPriceTitle'),
      buyoutDeviceWithPriceWaitingPriceNotificationText: t('details.endOfLeaseNotifications.waitingForBuyoutPriceMessage'),
      buyoutDeviceModalPurchaseAnywayTitle: t('details.midLeaseBuyout.withPrice.purchaseAssetAnywayModal.title'),
      buyoutDeviceModalPurchaseAnywayText1: t('details.midLeaseBuyout.withPrice.purchaseAssetAnywayModal.content.paragraph1'),
      buyoutDeviceModalPurchaseAnywayText2: t('details.midLeaseBuyout.withPrice.purchaseAssetAnywayModal.content.paragraph2'),
      buyoutDeviceModalPurchaseAnywayText3: t('details.midLeaseBuyout.withPrice.purchaseAssetAnywayModal.content.paragraph3'),
      buyoutDeviceModalPurchaseAnywayConfirmButton: t('details.midLeaseBuyout.withPrice.purchaseAssetAnywayModal.content.confirmButton'),
      buyoutDeviceModalPurchaseAnywayCancelButton: t('details.midLeaseBuyout.withPrice.purchaseAssetAnywayModal.content.cancelButton'),
      buyoutDevicePurchaseConfirmationCheckboxLabel: t('details.midLeaseBuyout.purchaseConfirmationCheckboxLabel'),
      buyoutDeviceModalNoPriceAvailableTitle: t('details.midLeaseBuyout.withPrice.priceNotAvailableModal.title'),
      buyoutDeviceModalNoPriceAvailableDescription: t('details.midLeaseBuyout.withPrice.priceNotAvailableModal.content.description'),
      buyoutDeviceModalNoPriceAvailableOptionsTitle: t('details.midLeaseBuyout.withPrice.priceNotAvailableModal.content.optionsTitle'),
      buyoutDeviceModalNoPriceAvailableOptionWait: t('details.midLeaseBuyout.withPrice.priceNotAvailableModal.content.optionWait'),
      buyoutDeviceModalNoPriceAvailableOptionPurchaseAnyway: t(
        'details.midLeaseBuyout.withPrice.priceNotAvailableModal.content.optionPurchaseAnyway',
      ),
      buyoutDeviceModalNoPriceAvailableOptionCancel: t('details.midLeaseBuyout.withPrice.priceNotAvailableModal.content.optionCancel'),
      buyoutDeviceModalNoPriceAvailableCancelButton: t('details.midLeaseBuyout.withPrice.priceNotAvailableModal.content.cancelButton'),
      buyoutDeviceModalNoPriceAvailableWaitButton: t('details.midLeaseBuyout.withPrice.priceNotAvailableModal.content.waitButton'),
      buyoutDeviceModalNoPriceAvailablePurchaseButton: t('details.midLeaseBuyout.withPrice.priceNotAvailableModal.content.purchaseButton'),
      buyoutDeviceModalPriceAvailableTitle: t('details.midLeaseBuyout.withPrice.priceAvailableModal.title'),
      buyoutDeviceModalPriceAvailableText1: t('details.midLeaseBuyout.withPrice.priceAvailableModal.generalInfo.paragraph1'),
      buyoutDeviceModalPriceAvailableText2: t('details.midLeaseBuyout.withPrice.priceAvailableModal.generalInfo.paragraph2'),
      buyoutDeviceModalPriceAvailableCancelButton: t('details.midLeaseBuyout.cancelButton'),
      buyoutDeviceModalPriceAvailableConfirmButton: t('details.midLeaseBuyout.confirmButton'),
    },
    return: {
      returnDeviceOptionTitle: t('details.midLeaseReturn.returnDeviceOptionTitle'),
      returnDeviceOptionText: t('details.midLeaseReturn.returnDeviceOptionText'),
      returnDeviceOptionButton: t('details.midLeaseReturn.returnDeviceOptionButton'),

      returnDeviceModalTitle: t('details.midLeaseReturn.returnDeviceModalTitle'),
      returnDeviceModalText1: t('details.midLeaseReturn.returnDeviceModalText1'),
      returnDeviceModalText2: t('details.midLeaseReturn.returnDeviceModalText2'),
      returnDeviceModal: {
        nextSteps: t('details.midLeaseReturn.returnDeviceModal.nextSteps'),
        step1: t('details.midLeaseReturn.returnDeviceModal.step1'),
        step2: t('details.midLeaseReturn.returnDeviceModal.step2'),
        step2Detail: t('details.midLeaseReturn.returnDeviceModal.step2Detail'),
        step3: t('details.midLeaseReturn.returnDeviceModal.step3'),
        step3Detail: t('details.midLeaseReturn.returnDeviceModal.step3Detail'),
        step4: t('details.midLeaseReturn.returnDeviceModal.step4'),
        step4Detail: t('details.midLeaseReturn.returnDeviceModal.step4Detail'),
      },
      returnDeviceModalContinueButton: t('details.midLeaseReturn.returnDeviceModalContinueButton'),
      returnDeviceModalCancelButton: t('details.midLeaseReturn.returnDeviceModalCancelButton'),

      returnDeviceOrderReturn: {
        headline: t('details.midLeaseReturn.returnDeviceReturnConfirmedHeadline'),
        checkoutDetails: {
          employeeTitle: t('details.midLeaseReturn.returnDeviceOrderReturnEmployeeTitle'),
          deliveryTitle: t('details.midLeaseReturn.returnDeviceOrderReturnDeliveryTitle'),
          deliveryText: t('details.midLeaseReturn.returnDeviceOrderReturnDeliveryText'),
          recipientText: t('details.midLeaseReturn.returnDeviceOrderReturnRecipientText'),
          contactText: t('details.midLeaseReturn.returnDeviceOrderReturnContactText'),
          deliveryQuestion: t('details.midLeaseReturn.returnDeviceOrderReturnDeliveryQuestion'),
          companyName: t('details.midLeaseReturn.returnDeviceOrderReturnCompanyName'),
          firstName: t('details.midLeaseReturn.returnDeviceOrderReturnFirstName'),
          lastName: t('details.midLeaseReturn.returnDeviceOrderReturnLastName'),
          email: t('details.midLeaseReturn.returnDeviceOrderReturnEmail'),
          phoneNbr: t('details.midLeaseReturn.returnDeviceOrderReturnPhoneNumber'),
          streetAddress: t('details.midLeaseReturn.returnDeviceOrderReturnStreet'),
          city: t('details.midLeaseReturn.returnDeviceOrderReturnCity'),
          postalCode: t('details.midLeaseReturn.returnDeviceOrderReturnPostalCode'),
          country: t('details.midLeaseReturn.returnDeviceOrderReturnCountry'),
          countryPlaceholder: t('details.midLeaseReturn.returnDeviceOrderReturnCountryPlaceholder'),
          homeDeliveryOption: t('details.midLeaseReturn.returnDeviceOrderReturnHomeDeliveryOption'),
          homeDeliveryOptionDetails: t('details.midLeaseReturn.returnDeviceOrderReturnHomeDeliveryOptionDetails'),
          companyDeliveryOption: t('details.midLeaseReturn.returnDeviceOrderReturnCompanyDeliveryOption'),
          companyDeliveryOptionDetails: t('details.midLeaseReturn.returnDeviceOrderReturnCompanyDeliveryOptionDetails'),
          required: t('details.midLeaseReturn.returnDeviceOrderReturnRequired'),
          invalidEmail: t('details.midLeaseReturn.returnDeviceOrderReturnInvalidEmail'),
          invalidPhoneNumber: t('details.midLeaseReturn.returnDeviceOrderReturnInvalidPhoneNumber'),
          invalidPostalCode: t('details.midLeaseReturn.returnDeviceOrderReturnInvalidPostalCode'),
          returnConfirmationCheckboxLabel: t('details.midLeaseReturn.returnDeviceConfirmationCheckboxLabel'),
          nextButton: t('details.midLeaseReturn.returnDeviceOrderReturnNextButton'),
          cancelButton: t('details.midLeaseReturn.returnDeviceOrderReturnCancelButton'),
          Finland: tGlobal('countries.Finland'),
          Sweden: tGlobal('countries.Sweden'),
          Norway: tGlobal('countries.Norway'),
          Denmark: tGlobal('countries.Denmark'),
          UnitedKingdom: tGlobal('countries.UnitedKingdom'),
        },
        returnableDevice: {
          title: t('details.midLeaseReturn.returnDeviceReturnableDeviceTitle'),
          text1: t('details.midLeaseReturn.returnDeviceReturnableDeviceText1'),
          text2: t('details.midLeaseReturn.returnDeviceReturnableDeviceText2'),
        },
      },

      returnDeviceReturnConfirmedHeadline: t('details.midLeaseReturn.returnDeviceReturnConfirmedHeadline'),
      returnDeviceReturnConfirmedNextButton: t('details.midLeaseReturn.returnDeviceReturnConfirmedNextButton'),
      returnDeviceReturnConfirmedText1: t('details.midLeaseReturn.returnDeviceReturnConfirmedText1'),
      returnDeviceReturnConfirmedText2: t('details.midLeaseReturn.returnDeviceReturnConfirmedText2'),
      returnDeviceReturnConfirmedText3: t('details.midLeaseReturn.returnDeviceReturnConfirmedText3'),
    },
  };

  const assignToUserTranslations: AssetAssignToUserTranslations = {
    deviceToBeAssigned: {
      name: t('details.assign.deviceToBeAssigned.name'),
      price: t('details.assign.deviceToBeAssigned.price'),
      currentUser: t('details.assign.deviceToBeAssigned.currentUser'),
      currentInvoicingAccount: t('details.assign.deviceToBeAssigned.currentInvoicingAccount'),
    },
    selectUserInput: {
      label: t('details.assign.selectUserInput.label'),
      placeholder: t('details.assign.selectUserInput.placeholder'),
    },
    userToAssignDeviceTo: {
      nameAndGroup: t('details.assign.userToAssignDeviceTo.nameAndGroup'),
      contact: t('details.assign.userToAssignDeviceTo.contact'),
      deviceLimit: t('details.assign.userToAssignDeviceTo.deviceLimit'),
      maxPricePaidByEmployer: t('details.assign.userToAssignDeviceTo.maxPricePaidByEmployer'),
    },
    newInvoicingAccount: {
      label: t('details.assign.newInvoicingAccount.label'),
      dropdownLabel: t('details.assign.newInvoicingAccount.dropdownLabelUser'),
      clearSelection: t('details.assign.newInvoicingAccount.clearSelection'),
      noOptionsFoundInfo: t('details.assign.newInvoicingAccount.noOptionsFoundInfo'),
      helperText: t('details.assign.newInvoicingAccount.helperText'),
    },
    vatInfo: t('details.vatIncluded'),
    month: t('details.assign.month'),
    months: t('details.assign.months'),
    unlimited: t('details.assign.unlimited'),
    leaseContinueInfo: {
      part1: t('details.assign.leaseContinueInfo.part1'),
      part2: t('details.assign.leaseContinueInfo.part2'),
    },
    modalHeading: t('details.assign.modalHeadingUser'),
    saveButton: t('details.assign.saveButton'),
    cancelButton: t('details.assign.cancelButton'),
    errors: {
      userDetailLoadingApiError: t('details.assign.errors.userDetailLoadingApiError'),
      cantBeAssignedBecauseOfSingleDevicePolicyError: t('details.assign.errors.cantBeAssignedBecauseOfSingleDevicePolicyError'),
      assignApiError: t('details.assign.errors.assignApiError'),
    },
    confirmationModal: {
      title: t('details.assign.confirmationModal.titleUser'),
      confirmInfo: t('details.assign.confirmationModal.confirmInfoUser'),
      confirmList: {
        device: t('details.assign.confirmationModal.confirmList.device'),
        price: t('details.assign.confirmationModal.confirmList.price'),
        remainingLeasePeriod: {
          remainingLeasePeriod: t('details.assign.confirmationModal.confirmList.remainingLeasePeriod.remainingLeasePeriod'),
          outOf: t('details.assign.confirmationModal.confirmList.remainingLeasePeriod.outOf'),
        },
        allowance: {
          allowance: t('details.assign.confirmationModal.confirmList.allowance.allowance'),
          possibleSalaryDeduction: t('details.assign.confirmationModal.confirmList.allowance.possibleSalaryDeduction'),
        },
        deviceLimit: t('details.assign.confirmationModal.confirmList.deviceLimit'),
      },
      vatInfo: t('details.assign.confirmationModal.vatIncluded'),
      month: t('details.assign.confirmationModal.month'),
      months: t('details.assign.confirmationModal.months'),
      confirmButton: t('details.assign.confirmationModal.confirmButton'),
      cancelButton: t('details.assign.confirmationModal.cancelButton'),
    },
    companyDevice: tGlobal('companyDevice'),
    inactiveUser: tGlobal('inactiveUser'),
  };

  const assignToOrganizationTranslations: AssetAssignToOrganizationTranslations = {
    modalHeading: t('details.assign.modalHeadingOrganization'),
    deviceToBeAssigned: {
      name: t('details.assign.deviceToBeAssigned.name'),
      price: t('details.assign.deviceToBeAssigned.price'),
      currentUser: t('details.assign.deviceToBeAssigned.currentUser'),
      currentInvoicingAccount: t('details.assign.deviceToBeAssigned.currentInvoicingAccount'),
    },
    newInvoicingAccount: {
      label: t('details.assign.newInvoicingAccount.label'),
      dropdownLabel: t('details.assign.newInvoicingAccount.dropdownLabelUser'),
      clearSelection: t('details.assign.newInvoicingAccount.clearSelection'),
      noOptionsFoundInfo: t('details.assign.newInvoicingAccount.noOptionsFoundInfo'),
      helperText: t('details.assign.newInvoicingAccount.helperText'),
    },
    leaseContinueInfo: {
      part1: t('details.assign.leaseContinueInfo.part1'),
      part2: t('details.assign.leaseContinueInfo.part2'),
    },
    confirmationModal: {
      title: t('details.assign.confirmationModal.titleOrganization'),
      confirmInfo: t('details.assign.confirmationModal.confirmInfoOrganization'),
      confirmList: {
        device: t('details.assign.confirmationModal.confirmList.device'),
        price: t('details.assign.confirmationModal.confirmList.price'),
        remainingLeasePeriod: {
          remainingLeasePeriod: t('details.assign.confirmationModal.confirmList.remainingLeasePeriod.remainingLeasePeriod'),
          outOf: t('details.assign.confirmationModal.confirmList.remainingLeasePeriod.outOf'),
        },
      },
      vatInfo: t('details.assign.confirmationModal.vatIncluded'),
      month: t('details.assign.confirmationModal.month'),
      months: t('details.assign.confirmationModal.months'),
      confirmButton: t('details.assign.confirmationModal.confirmButton'),
      cancelButton: t('details.assign.confirmationModal.cancelButton'),
    },
    vatInfo: t('details.assign.confirmationModal.vatIncluded'),
    month: t('details.assign.confirmationModal.month'),
    months: t('details.assign.confirmationModal.months'),
    unlimited: t('details.assign.unlimited'),
    saveButton: t('details.assign.saveButton'),
    cancelButton: t('details.assign.confirmationModal.cancelButton'),
    errors: {
      assignApiError: t('details.assign.errors.assignApiError'),
    },
    companyDevice: tGlobal('companyDevice'),
    inactiveUser: tGlobal('inactiveUser'),
  };

  const fetchInvoicingAccounts = async (organizationId: string) => {
    setInvoicingAccountsError(false);
    setLoadingInvoicingAccounts(true);
    try {
      const res = await assetAdminInvoicingAccountClient.adminReadInvoicingAccountOptions(organizationId, 99999);
      setInvoicingAccounts(res.data.contents);
    } catch (err) {
      console.error(err);
      setInvoicingAccountsError(true);
    } finally {
      setLoadingInvoicingAccounts(false);
    }
  };

  const fetchAssetActions = async (organizationId: string, assetId: string) => {
    setAssetActionsLoading(true);
    try {
      const assetActions = (await assetAdminClient.adminOrganizationAssetGetActions(organizationId, assetId)).data;
      setAssetActions(assetActions);
    } catch (err) {
      console.error(err);
      setAssetActions(undefined);
    }
    setAssetActionsLoading(false);
  };

  const fetchCustomerOrganizationById = async (organizationId?: string) => {
    setLoading(true);
    try {
      if (organizationId) {
        const organizationResponse = await assetAdminOrganizationClient.adminOrganizationRead(organizationId, 'user_groups,user_groups.users');
        // Use an exist order store for storing organization info
        dispatch(setOrderOrganization(organizationResponse.data));
      }
    } catch (err) {
      console.error(err);
      setApiError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserAssetsById = async (userId: string) => {
    if (!userId) {
      console.error("No userId - can't fetch user's assets");
      return;
    }

    try {
      const user = (await assetAdminUserClient.adminUserOfUsersRead(organizationId, userId)).data;
      setUser(user);
      const userAssets = (await assetAdminClient.adminUserAssetsList(organizationId, user.user_group.id, userId, ASSET_MAX_LIMIT)).data.contents;
      setUserAssets(userAssets);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssetById = async (assetId: string, organizationId: string) => {
    try {
      const response = await assetAdminClient.adminOrganizationAssetRead(organizationId, assetId);
      setAsset(response.data);
      const userId = response.data.end_user.user?.id;
      fetchUserAssetsById(userId || '');
      fetchAssetActions(organizationId, response.data.id);
    } catch (err) {
      console.error(err);
      setApiError(true);
    } finally {
      setLoading(false);
    }
  };

  const checkAssetSyncError = async (): Promise<void> => {
    try {
      const assetList = (await assetAdminClient.adminListAssets(ASSET_MAX_LIMIT, 0, [FinanceSyncStatus.Error], undefined)).data.contents;
      setAssetSyncError(assetList.some(asset => asset.id === assetId));
    } catch {
      setAssetSyncError(false);
    }
  };

  useEffect(() => {
    if (!organization || organization.id !== organizationId) {
      fetchCustomerOrganizationById(organizationId);
    }
    fetchAssetById(assetId, organizationId);
    fetchInvoicingAccounts(organizationId);
    // Telia Finance sync error is only informative and not critical to block the page loading
    checkAssetSyncError().catch(() => {});
  }, []);

  const executeAssetAction = async (action: SelectedAssetAction): Promise<AdminOrganizationAssetResponse | undefined> => {
    if (action.serialNumber) {
      return (
        await assetAdminClient.adminOrganizationAssetReplaceSerialNumber(organizationId, assetId, {
          number: action.serialNumber,
          type: asset?.serial_number.type! as unknown as SerialNumberRequestTypeEnum,
        })
      ).data;
    }
    if (action.cancelTermination) {
      return (await assetAdminClient.adminOrganizationAssetCancelEolChoice(organizationId, assetId)).data;
    }
    if (action.financeSyncRequest) {
      await assetAdminClient.adminTriggerAssetFinanceSync(organizationId, assetId);
      return asset;
    }
    if (action.endOfLease) {
      const eolDecisionChange: AdminAssetManuallyReportedEndOfLeaseRequest = {
        buyout: action.endOfLease?.buyout ? {} : undefined,
        return: action.endOfLease?.return ? {} : undefined,
        extension: action.endOfLease?.extension?.duration ? { length: action.endOfLease.extension.duration } : undefined,
      };
      return (await assetAdminClient.adminOrganizationAssetChangeEolChoice(organizationId, assetId, eolDecisionChange)).data;
    }
    if (action.midLease) {
      const midLeaseTerminationRequest: AssetMidLeaseTerminationRequest = {
        buyout: action.midLease.buyout ? {} : undefined,
        return: action.midLease.return ? {} : undefined,
        lease_ended_at: action.midLease?.date || undefined,
      };
      return (await assetAdminClient.adminSaveMidLeaseTermination(organizationId, assetId, midLeaseTerminationRequest)).data;
    }
    // Not supported action.
    return undefined;
  };

  const handleOnAdminAssetActionSave = async (action: SelectedAssetAction) => {
    setAssetActionsLoading(true);
    try {
      const updatedAsset = await executeAssetAction(action);
      if (!updatedAsset) return;
      setAsset(updatedAsset);
      fetchAssetActions(organizationId, assetId);
    } catch (error) {
      console.error(error);
      setAssetActionsPostError(true);
    } finally {
      setAssetActionsLoading(false);
    }
  };

  const handleCreateTicket = () => {
    let url = process.env.REACT_APP_TICKET_URI;
    switch (organization?.locale.language.split('-')[0]) {
      case 'no':
        url = process.env.REACT_APP_TICKET_URI_NO;
        break;
      case 'se':
        url = process.env.REACT_APP_TICKET_URI_SE;
        break;
      case 'fi':
        url = process.env.REACT_APP_TICKET_URI_FI;
    }
    createTicket(url, asset);
  };

  const handleAssignToDifferentUserInitialization = async () => {
    const getOrganizationAllUsers = async () => {
      setOrganizationUsersLoading(true);
      setOrganizationUsersFetchError(false);
      try {
        const customerOrgResponse = await assetAdminOrganizationClient.adminOrganizationRead(organizationId, 'user_groups,user_groups.users');
        const orgUsers = flattenOrganizationUsers(customerOrgResponse.data);
        // Handle a mismatch between undefined and null on users phone number typescript would complain about
        setOrganizationUsers(
          orgUsers.map(user => ({
            ...user,
            phone_number: user.phone_number ?? undefined,
          })),
        );
        setAssignToDifferentUserModalOpen(true);
      } catch {
        setOrganizationUsersFetchError(true);
      } finally {
        setOrganizationUsersLoading(false);
      }
    };
    // If user opens and closes the asset assign modal multiple times, get users from backend only on the first time
    if (organizationUsers.length === 0) {
      getOrganizationAllUsers();
    } else {
      setAssignToDifferentUserModalOpen(true);
    }
  };

  const handleTargetUserSelected = async (user: User): Promise<UserDetails> => {
    try {
      const responses = await Promise.all([
        assetAdminUserClient.adminUserRead(organizationId, user?.user_group?.id || '', user?.id || ''),
        assetAdminUserGroupClient.adminUserGroupRead(organizationId, user?.user_group?.id || ''),
      ]);
      const resUser = responses[0];
      const resUserGroup = responses[1];
      return {
        ...resUser.data,
        user_group: resUserGroup.data,
      };
    } catch {
      // Reusable AssetAssignToUser component assumes this callback to throw if target user details fetching fails
      throw new Error('Fetching target user details failed');
    }
  };

  const handleAssignToDifferentUser = async (user: UserDetails, invoicingAccountId?: string): Promise<boolean> => {
    setAssetAssignLoading(true);
    let success = false;
    try {
      const assetResponse = await assetAdminClient.adminOrganizationAssetAssignUser(organizationId, asset?.id || '', {
        user_id: user.id,
        invoicing_account: invoicingAccountId,
      });
      setAssignToDifferentUserModalOpen(false);
      setAssetAssignSuccess(true);
      setAsset(assetResponse.data);
      // Clear previous owner's assets and fetch new owner's assets
      setUserAssets([]);
      const userId = assetResponse.data.end_user.user?.id;
      if (userId) {
        fetchUserAssetsById(userId);
      }
      success = true;
    } catch {
      success = false;
      fetchAssetById(assetId, organizationId);
    } finally {
      setAssetAssignLoading(false);
    }
    return success;
  };

  const handleAssignToOrganization = async (invoicingAccountId?: string): Promise<boolean> => {
    setAssetAssignLoading(true);
    let success = false;
    try {
      const assetResponse = await assetAdminClient.adminOrganizationAssetAssignUser(organizationId, asset?.id || '', {
        user_id: null,
        invoicing_account: invoicingAccountId,
      });
      setAssignToOrganizationModalOpen(false);
      setAssetAssignSuccess(true);
      setAsset(assetResponse.data);
      // Clear previous owner's assets
      setUserAssets([]);
      success = true;
    } catch {
      success = false;
      fetchAssetById(assetId, organizationId);
    } finally {
      setAssetAssignLoading(false);
    }
    return success;
  };

  const loader = (centered = false, dimBackground = false) => {
    const loader = <Loader {...(centered ? { style: { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' } } : {})} />;
    if (dimBackground) {
      return <Div className={styles['asset-transfer-loading-dim-overlay']}>{loader}</Div>;
    }
    return loader;
  };

  const getUserLink = (orgId: string, userGroupId: string, userId: string) => `/customer/${orgId}/usergroups/${userGroupId}/users/${userId}`;

  const currentUserLink = (): (() => string) | undefined => {
    const currentUser = organizationUsers.find(orgUser => orgUser?.id && orgUser.id === asset?.end_user.user?.id);
    if (currentUser?.id && currentUser.user_group?.id) {
      const link = getUserLink(organizationId, currentUser.user_group.id, currentUser.id);
      return () => link;
    }
    return undefined;
  };

  const handleSaveLocationReference = async (newLocationReference: string) => {
    if (asset) {
      try {
        const updatedAsset = await assetAdminClient.adminOrganizationAssetUpdate(organizationId, asset.id, {
          location_reference: newLocationReference || null,
          invoicing_account: asset.invoicing_account?.id,
        });
        setAsset(updatedAsset.data);
      } catch {
        setAssetUpdatePutError(true);
      }
    }
  };

  const handleSaveInvoicingAccount = async (invoicingAccount?: InvoicingAccountOptionResponseContents | InvoicingAccountResponse | undefined) => {
    if (asset) {
      try {
        const updatedAsset = await assetAdminClient.adminOrganizationAssetUpdate(organizationId, asset.id, {
          location_reference: asset.location_reference,
          invoicing_account: invoicingAccount?.id || null,
        });
        setAsset(updatedAsset.data);
      } catch {
        setAssetUpdatePutError(true);
      }
    }
  };

  const handleCreateDraftDecisionRequest = async (isMidleaseDecision: boolean) => {
    try {
      const req = { buyout: {}, confirm: false };
      const res = (
        await (isMidleaseDecision
          ? assetMidLeaseClient.adminSaveOrganizationAssetMidLeaseChoice(organizationId, assetId, req)
          : assetEndOfLeaseClient.adminSaveOrganizationAssetEolChoice(organizationId, assetId, req))
      ).data;
      if (!res?.unconfirmed_end_of_lease_decisions?.length) {
        setNotification({ show: true, text: t('details.endOfLeaseGrowlNotifications.priceRequestError'), variant: 'error' });
        return undefined;
      }
      return res.unconfirmed_end_of_lease_decisions;
    } catch {
      setNotification({ show: true, text: t('details.endOfLeaseGrowlNotifications.priceRequestError'), variant: 'error' });
    }
  };

  const handleConfirmOrCancelDraftDecisionRequest = async (isMidleaseDecision: boolean, decisionId: string, confirm: boolean) => {
    try {
      const req = { confirm };
      const res = (
        await (isMidleaseDecision
          ? assetMidLeaseClient.adminConfirmOrganizationAssetMidLeaseChoice(organizationId, assetId, decisionId, req)
          : assetEndOfLeaseClient.adminConfirmOrganizationAssetEndOfLease(organizationId, assetId, decisionId, req))
      ).data;
      if (!confirm) {
        setNotification({ show: true, text: t('details.endOfLeaseGrowlNotifications.priceRequestCancelled'), variant: 'warning' });
      }
      return !!res;
    } catch {
      setNotification({ show: true, text: t('details.endOfLeaseGrowlNotifications.decisionDraftConfirmOrCancelError'), variant: 'error' });
      return false;
    }
  };

  const handleConfirmDecisionRequest = async (request: AssetEndOfLeaseRequest) => {
    try {
      // Handle eol return decision
      if (request.return) {
        await assetEndOfLeaseClient.adminSaveOrganizationAssetEolChoice(organizationId, assetId, {
          return: request.return,
          confirm: request.confirm,
        });
        return true;
      } else {
        // Handle other than return eol decision
        await assetEndOfLeaseClient.adminSaveOrganizationAssetEolChoice(organizationId, assetId, {
          buyout: request.buyout,
          extension: request.extension,
          confirm: request.confirm,
        });
        return true;
      }
    } catch {
      setNotification({ show: true, text: t('details.endOfLeaseGrowlNotifications.decisionError'), variant: 'error' });
      return false;
    }
  };

  const handleEndOfLeaseFlowFinish = (selectedChoice: EolFlowFinishFromChoice) => {
    setNotification({ show: true, text: t('details.endOfLeaseGrowlNotifications.decisionSuccessful'), variant: 'success' });
    switch (selectedChoice) {
      case 'upgrade':
        if (user) history.push(getUserLink(organizationId, user.user_group.id, user.id));
        else fetchAssetById(assetId, organizationId);
        break;
      default:
        fetchAssetById(assetId, organizationId);
        break;
    }
  };

  const handleMidLeaseConfirmDecisionRequest = async (request: AssetMidLeaseRequest) => {
    try {
      const requestPayload = request.return ? { return: request.return } : { buyout: request.buyout };
      await assetMidLeaseClient.adminSaveOrganizationAssetMidLeaseChoice(organizationId, assetId, requestPayload);
      return true;
    } catch {
      setNotification({ show: true, text: t('details.midLeaseGrowlNotifications.decisionError'), variant: 'error' });
      return false;
    }
  };

  const handleMidLeaseFlowFinish = (_: MidLeaseFlowFinishFromChoice) => {
    setNotification({ show: true, text: t('details.midLeaseGrowlNotifications.decisionSuccessful'), variant: 'success' });
    fetchAssetById(assetId, organizationId);
  };

  const isAssignableToOrganization = (asset: AdminOrganizationAssetResponse) => asset.assign_user_allowed && asset.end_user.user;

  const createSecondTicketModalContent = () => <BodyText>{t('details.secondTicketModal.content')}</BodyText>;

  const handleOnLinkClick = () => {
    if (!organizationId) {
      return;
    }
    history.push(withCustomerOrgFormPath(routes.devices, organizationId));
  };

  const TopContent = (
    <Div padding={{ vertical: 'md' }}>
      <PageHeader linkText={t('details.goBackToList')} onLinkClick={handleOnLinkClick}>
        {t('details.deviceInformationTitle')}
      </PageHeader>
      {assetSyncError && (
        <>
          <Gutter />
          <Tag backgroundColor="red600">{t('details.assetSyncError')}</Tag>
        </>
      )}
    </Div>
  );

  if (loading || loadingInvoicingAccounts) {
    return loader(true);
  } else if (apiError) {
    return <Message text={t('apiError')} />;
  } else if (!asset) {
    return <Message text={t('details.assetNotFound', { id: assetId })} />;
  }

  return (
    <>
      {TopContent}
      <OrganizationAssetCard
        companyName={organization?.name || ''}
        asset={asset}
        assetUserLink={user ? getUserLink(organizationId, user.user_group.id, user.id) : undefined}
        isOpen={true}
        handleCreateTicket={handleCreateTicket}
        customSecondTicketModalContent={createSecondTicketModalContent()}
        userAssets={userAssets}
        currency={organization?.locale.currency.toUpperCase() || ''}
        translations={translations}
        endOfLeaseOptions={{
          buyoutWithPriceFlow: useBuyoutWithPriceFlow,
          showUpgrade: true,
          handleConfirmDecisionRequest: handleConfirmDecisionRequest,
          handleConfirmOrCancelDraftDecisionRequest: (endOfLeaseId: string, confirm: boolean) =>
            handleConfirmOrCancelDraftDecisionRequest(false, endOfLeaseId, confirm),
          handleCreateDraftDecisionRequest: () => handleCreateDraftDecisionRequest(false),
          handleEndOfLeaseFlowFinish: handleEndOfLeaseFlowFinish,
          // Allow return only for Norway addresses
          countries: [COUNTRIES.find(country => country.value === 'NO')!],
          user,
        }}
        midLeaseOptions={{
          buyoutWithPriceFlow: useBuyoutWithPriceFlow,
          handleConfirmDecisionRequest: handleMidLeaseConfirmDecisionRequest,
          handleConfirmOrCancelDraftDecisionRequest: (midLeaseId: string, confirm: boolean) =>
            handleConfirmOrCancelDraftDecisionRequest(true, midLeaseId, confirm),
          handleCreateDraftDecisionRequest: () => handleCreateDraftDecisionRequest(true),
          handleMidLeaseFlowFinish: handleMidLeaseFlowFinish,
          translations: midLeaseChoiceTranslations,
        }}
        handleAssignToDifferentUser={asset.assign_user_allowed ? handleAssignToDifferentUserInitialization : undefined}
        handleAssignToOrganization={isAssignableToOrganization(asset) ? () => setAssignToOrganizationModalOpen(true) : undefined}
        adminAssetActions={{
          adminAssetActionsResponse: assetActions,
          onAdminAssetActionSave: handleOnAdminAssetActionSave,
          isSaveButtonDisabled: assetActionsLoading,
        }}
        handleSaveLocationReference={handleSaveLocationReference}
        fetchInvoicingAccounts={() => Promise.resolve(invoicingAccounts)}
        handleSaveInvoicingAccount={handleSaveInvoicingAccount}
      />
      {organizationUsersLoading && loader(true)}
      {organizationUsersFetchError && (
        <GrowlNotification
          notification={t('details.assign.errorFetchingOrganizationUsers')}
          variant={'error'}
          closeOnClick={() => setOrganizationUsersFetchError(false)}
        />
      )}
      {assetAssignLoading && loader(true, true)}
      {assignToDifferentUserModalOpen && (
        <AssetAssignToUser
          translations={assignToUserTranslations}
          assetToTransfer={asset}
          possibleTargetUsers={organizationUsers}
          onTargetUserSelect={handleTargetUserSelected}
          onSave={handleAssignToDifferentUser}
          onCancel={() => setAssignToDifferentUserModalOpen(false)}
          showConfirmation={true}
          invoicingAccounts={invoicingAccounts}
          getTargetUserLink={user => getUserLink(organizationId, user.user_group.id, user.id)}
          getCurrentUserLink={currentUserLink()}
          currency={organization?.locale.currency || ''}
        />
      )}
      {organization && assignToOrganizationModalOpen && (
        <AssetAssignToOrganization
          translations={assignToOrganizationTranslations}
          asset={asset}
          targetOrganization={organization}
          onSave={handleAssignToOrganization}
          onCancel={() => setAssignToOrganizationModalOpen(false)}
          showConfirmation={true}
          invoicingAccounts={invoicingAccounts}
          getCurrentUserLink={currentUserLink()}
          currency={organization?.locale.currency || ''}
        />
      )}
      {assetAssignSuccess && (
        <GrowlNotification
          notification={t('details.assign.assetAssignSuccessful')}
          variant={'success'}
          closeOnClick={() => setAssetAssignSuccess(false)}
        />
      )}
      {assetActionsPostError && (
        <GrowlNotification notification={t('details.assetActionPostError')} variant={'error'} closeOnClick={() => setAssetActionsPostError(false)} />
      )}
      {assetUpdatePutError && (
        <GrowlNotification notification={t('details.assetUpdatePutError')} variant={'error'} closeOnClick={() => setAssetUpdatePutError(false)} />
      )}

      {notification?.show && (
        <GrowlNotification
          notification={notification?.text}
          variant={notification?.variant}
          closeOnClick={() => setNotification({ show: false, text: '', variant: 'success' })}
        />
      )}

      {invoicingAccountsError && (
        <GrowlNotification
          notification={tError('invoicingAccount.fetchFailed')}
          variant={'error'}
          closeOnClick={() => setInvoicingAccountsError(false)}
        />
      )}
    </>
  );
};
